import { traduccionesVisibles } from './panel-interpretacion';

/**
 * Dos ayudas para leer una fila del arancel sin conocer el vocabulario del
 * libro. Son de presentación: no tocan el orden ni la búsqueda.
 */

export interface Tramo {
  texto: string;
  resaltado: boolean;
}

/**
 * Sin tildes y en minúscula, guardando de qué carácter del original salió cada
 * carácter normalizado.
 *
 * El mapa hace falta porque sacar las tildes cambia los largos: sin él, el
 * tramo resaltado se correría unos caracteres justo en las palabras acentuadas,
 * que son las que más importa marcar ("metálica", "protección").
 */
function normalizarConMapa(texto: string): { norm: string; origen: number[] } {
  let norm = '';
  const origen: number[] = [];
  for (let i = 0; i < texto.length; i++) {
    const limpio = texto[i].normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    for (const ch of limpio) {
      norm += ch;
      origen.push(i);
    }
  }
  return { norm, origen };
}

function normalizar(texto: string): string {
  return normalizarConMapa(texto).norm;
}

/**
 * Las palabras del término, en orden, cada una pudiendo seguir con más letras.
 *
 * Sin esto casi nada se marcaría: la IA devuelve "que cubra el tobillo" y el
 * arancel escribe "Que cubran el tobillo". Es el mismo criterio con el que el
 * backend verifica los términos contra el catálogo (`contarApariciones`), y por
 * la misma razón — solo que acá, si se desincroniza, lo único que pasa es que
 * un resaltado no aparece.
 *
 * El sufijo son letras o dígitos, no `\S`: si no, marcar "calzado" en
 * "calzados," se llevaría puesta la coma.
 */
function patronDeTermino(termino: string): RegExp | null {
  const palabras = normalizar(termino)
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (palabras.length === 0) return null;
  const sufijo = '[\\p{L}\\p{N}]*';
  return new RegExp(palabras.join(`${sufijo}\\s+`) + sufijo, 'gu');
}

/**
 * Parte el texto en tramos, marcando los que matchearon algún término.
 *
 * Es lo que contesta "¿por qué apareció esta fila?" sin que el usuario tenga
 * que comparar a ojo su búsqueda contra la descripción del arancel.
 */
export function partirResaltando(texto: string, terminos: string[]): Tramo[] {
  if (!texto) return [];

  const { norm, origen } = normalizarConMapa(texto);
  const rangos: Array<[number, number]> = [];

  for (const termino of terminos) {
    const patron = patronDeTermino(termino);
    if (!patron) continue;
    for (const m of norm.matchAll(patron)) {
      if (!m[0]) continue;
      const desde = m.index!;
      // Del primer carácter original al último, +1 para que sea un corte.
      rangos.push([origen[desde], origen[desde + m[0].length - 1] + 1]);
    }
  }

  if (rangos.length === 0) return [{ texto, resaltado: false }];

  // Dos términos que se pisan ("puntera metálica" y "metálica de protección")
  // tienen que dar UN tramo, no tres pedazos rotos.
  rangos.sort((a, b) => a[0] - b[0]);
  const unidos: Array<[number, number]> = [rangos[0]];
  for (const [ini, fin] of rangos.slice(1)) {
    const ultimo = unidos[unidos.length - 1];
    if (ini <= ultimo[1]) ultimo[1] = Math.max(ultimo[1], fin);
    else unidos.push([ini, fin]);
  }

  const tramos: Tramo[] = [];
  let cursor = 0;
  for (const [ini, fin] of unidos) {
    if (ini > cursor) tramos.push({ texto: texto.slice(cursor, ini), resaltado: false });
    tramos.push({ texto: texto.slice(ini, fin), resaltado: true });
    cursor = fin;
  }
  if (cursor < texto.length) tramos.push({ texto: texto.slice(cursor), resaltado: false });
  return tramos;
}

/**
 * Qué palabra del usuario dio origen al texto de esta fila, si es que alguna.
 *
 * El caso del rework: la partida correcta dice "Que cubran el tobillo" y el
 * usuario escribió "botas". Sin decirlo en la fila, descarta la partida
 * correcta por no reconocer cómo la nombra el libro — y esa es exactamente la
 * clase de error que el rework existe para evitar.
 *
 * Distinto de la línea "Traduje" del panel de arriba: esa explica la búsqueda
 * entera, esta explica UNA fila.
 */
export function aclaracionDeVocabulario(
  textoDeLaFila: string,
  traducciones: Array<{ de: string; a: string }> | undefined,
  nucleo?: string
): string | null {
  const texto = normalizar(textoDeLaFila);
  const cabeza = nucleo ? normalizar(nucleo) : '';

  const match = traduccionesVisibles(traducciones).find((t) => {
    // La traducción del sustantivo núcleo la comparten TODOS los resultados
    // —buscando "zapatos" el arancel dice "calzado" en las quince filas—, así
    // que aclararla fila por fila deja de aclarar y pasa a ser empapelado. Esa
    // ya la explica la línea "Traduje" del panel, una sola vez. Acá quedan los
    // calificadores, que son los que separan una partida de su hermana.
    if (cabeza && normalizar(t.a) === cabeza) return false;
    const patron = patronDeTermino(t.a);
    return patron !== null && patron.test(texto);
  });
  return match?.de ?? null;
}
